import {Truncate} from '@dropbox/dig-components/truncate';
import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack, withShade} from '@dropbox/dig-foundations';
import {GroupTabs} from 'components/DSYS/GroupTabs';
import {LabelGroup} from 'components/DSYS/LabelGroup';
import {useSkeletonFadeIn} from 'components/DSYS/SkeletonFadeIn';
import {StatusChip} from 'components/DSYS/StatusChip';
import {t} from 'i18next';
import {useAtom} from 'jotai';
import {useEffect, useMemo, useState} from 'react';
import {DashboardState, JiraTicket, JiraTicketTabId} from 'views/home/dashboard/dashboardModel';
import {Tile} from 'views/home/dashboard/Tile';
import {TileHeaderLink} from 'views/home/dashboard/TileListFooter';
import {TileListRow} from 'views/home/dashboard/TileListRow';
import {TileRowLink} from 'views/home/dashboard/TileRowLink';
import {TileListSkeleton} from 'views/home/dashboard/tiles/TileListSkeleton';
import {TileScroller} from 'views/home/dashboard/TileScroller';

import {jiraDefaultTabAtom} from './jiraDefaultTabAtom';
import {
  availableJiraStatuses,
  filterJiraTicketsByStatus,
  toggleJiraStatus,
} from './jiraStatusFilter';
import {jiraStatusFilterAtom} from './jiraStatusFilterAtom';
import {
  ASSIGNED_TAB_ID,
  countJiraTicketsForTab,
  CREATED_TAB_ID,
  filterJiraTicketsForTab,
  getDefaultJiraTab,
  shouldShowJiraTabs,
} from './jiraTicketTabs';
import styles from './JiraTile.module.css';
import {JiraTileMenu} from './JiraTileMenu';

const formatTicketSubtext = (ticket: JiraTicket): string =>
  ticket.counterpartyName ? `${ticket.counterpartyName} • ${ticket.key}` : ticket.key;

const JiraTicketRow = ({ticket}: {ticket: JiraTicket}) => {
  const shade = withShade({
    style: {marginInline: -8, cursor: ticket.url ? 'pointer' : undefined},
  });
  const row = (
    <TileListRow shade={shade} trailing={<StatusChip status={ticket.status} />}>
      <LabelGroup
        align="top"
        withText={
          <Text isBold tagName="div" style={{minWidth: 0}}>
            <Truncate observeParent>{ticket.title}</Truncate>
          </Text>
        }
        withSubtext={
          <Text size="small" color="faint" tagName="div" style={{minWidth: 0}}>
            <Truncate observeParent>{formatTicketSubtext(ticket)}</Truncate>
          </Text>
        }
      />
    </TileListRow>
  );

  return ticket.url ? <TileRowLink href={ticket.url}>{row}</TileRowLink> : <Box>{row}</Box>;
};

const JiraTicketList = ({
  tickets,
  tabId,
  showTabs,
}: {
  tickets: JiraTicket[];
  tabId: JiraTicketTabId;
  /** When false, show the full list (only one relevant category). */
  showTabs: boolean;
}) => {
  const visible = showTabs ? filterJiraTicketsForTab(tickets, tabId) : tickets;

  if (visible.length === 0) {
    return <Text color="faint">{t('dashboard_jira_empty_filtered')}</Text>;
  }

  return (
    <TileScroller>
      {visible.map((ticket) => (
        <JiraTicketRow key={ticket.id} ticket={ticket} />
      ))}
    </TileScroller>
  );
};

export const JiraTile = ({state}: {state: DashboardState}) => {
  const bodyRef = useSkeletonFadeIn(state.jiraLoading);
  const [statusFilter, setStatusFilter] = useAtom(jiraStatusFilterAtom);
  // Remember which tab the viewer prefers to open on (localStorage).
  const [defaultTabPreference, setDefaultTabPreference] = useAtom(jiraDefaultTabAtom);
  const availableStatuses = useMemo(
    () => availableJiraStatuses(state.jiraTickets),
    [state.jiraTickets]
  );
  const filteredTickets = useMemo(
    () => filterJiraTicketsByStatus(state.jiraTickets, statusFilter, availableStatuses),
    [availableStatuses, state.jiraTickets, statusFilter]
  );
  // Tabs only when both assigned and created tickets survive the status filter.
  const showTabs = useMemo(() => shouldShowJiraTabs(filteredTickets), [filteredTickets]);
  const defaultTab = useMemo(
    () => getDefaultJiraTab(filteredTickets, defaultTabPreference),
    [filteredTickets, defaultTabPreference]
  );
  const [activeTab, setActiveTab] = useState<JiraTicketTabId>(defaultTab);

  useEffect(() => {
    setActiveTab((current) => {
      if (
        (current === ASSIGNED_TAB_ID &&
          countJiraTicketsForTab(filteredTickets, ASSIGNED_TAB_ID) > 0) ||
        (current === CREATED_TAB_ID && countJiraTicketsForTab(filteredTickets, CREATED_TAB_ID) > 0)
      ) {
        return current;
      }
      return defaultTab;
    });
  }, [defaultTab, filteredTickets]);

  const tabs = useMemo(
    () => [
      {
        id: ASSIGNED_TAB_ID,
        label: t('dashboard_jira_tab_assigned'),
        count: countJiraTicketsForTab(filteredTickets, ASSIGNED_TAB_ID),
        disabled: false,
      },
      {
        id: CREATED_TAB_ID,
        label: t('dashboard_jira_tab_created'),
        count: countJiraTicketsForTab(filteredTickets, CREATED_TAB_ID),
        disabled: false,
      },
    ],
    [filteredTickets]
  );

  const statusMenu = (
    <JiraTileMenu
      statusFilter={statusFilter}
      availableStatuses={availableStatuses}
      onToggleStatus={(status) => setStatusFilter((prev) => toggleJiraStatus(prev, status))}
      showTabs={showTabs}
      defaultTab={defaultTabPreference}
      onDefaultTabChange={setDefaultTabPreference}
    />
  );

  const jiraHeaderLink = state.jiraIssuesUrl ? (
    <TileHeaderLink to={state.jiraIssuesUrl} />
  ) : undefined;

  if (state.jiraLoading) {
    return (
      <Tile tileId="jira">
        <TileListSkeleton bare accessory="none" trailing="chip" rows={4} />
      </Tile>
    );
  }

  if (state.jiraTickets.length === 0) {
    return (
      <Tile tileId="jira" bodyRef={bodyRef}>
        <Text color="faint">{t('dashboard_jira_empty')}</Text>
      </Tile>
    );
  }

  const list = <JiraTicketList tickets={filteredTickets} tabId={activeTab} showTabs={showTabs} />;

  if (!showTabs) {
    return (
      <Tile tileId="jira" labelAccessory={statusMenu} accessory={jiraHeaderLink} bodyRef={bodyRef}>
        <Stack gap="4">{list}</Stack>
      </Tile>
    );
  }

  return (
    <Tile tileId="jira" labelAccessory={statusMenu} accessory={jiraHeaderLink} bodyRef={bodyRef}>
      <Stack gap="8" display="flex" height="100%">
        <div className={styles.groupTabsContainer}>
          <GroupTabs
            tabs={tabs}
            activeTab={activeTab}
            onTabClick={(tabId) => tabId && setActiveTab(tabId as JiraTicketTabId)}
            totalCount={filteredTickets.length}
            showAllTab={false}
          />
        </div>
        <Box display="flex" flexDirection="column" style={{flex: 1, minHeight: 0}}>
          {list}
        </Box>
      </Stack>
    </Tile>
  );
};
